/**
 * Sport Configuration
 *
 * This file contains sport configuration data (types, match settings, etc.)
 * For component mappings, see scoringComponents.js
 */

/**
 * Sport Types Enum
 * Centralized constants for sport names to avoid hardcoding strings
 * Use these constants throughout the application for consistency
 */
export const SPORT_TYPES = {
  // Card/Casino Games
  POKER: 'poker',

  // Racquet Sports
  PICKLEBALL: 'pickleball',
  TENNIS: 'tennis',

  // Bat and Ball Sports
  CRICKET: 'cricket',

  // Table Sports
  PING_PONG: 'ping pong',
  TABLE_TENNIS: 'table tennis',

  // Racquet Sports (full implementation)
  BADMINTON: 'badminton',

  // Team Sports (future)
  BASKETBALL: 'basketball',
  VOLLEYBALL: 'volleyball',
  SOCCER: 'soccer',
  FOOTBALL: 'football',
};

/**
 * Participant type field for sports that support multiple formats.
 * Only one option = auto-hidden + auto-applied (e.g. team-only sports).
 */
const PARTICIPANTS_TYPE_TEAMS_ONLY = {
  name: 'participantsType',
  type: 'select',
  label: 'Participants Type',
  options: [{ value: 'teams', label: 'Teams' }],
  default: 'teams',
  required: true
};

const PARTICIPANTS_TYPE_BOTH = {
  name: 'participantsType',
  type: 'select',
  label: 'Participants Type',
  options: [
    { value: 'singles', label: 'Singles (1v1)' },
    { value: 'doubles', label: 'Doubles (2v2)' }
  ],
  default: 'singles',
  required: true
};

/**
 * Whether a scorer can mid-match swap in a different roster player for one
 * currently playing. Racquet/individual sports default this off — real
 * singles/doubles matches don't allow substitutions — but a specific match
 * can still turn it on via a per-match settings override (e.g. an MLP
 * Dreambreaker, played singles even though the team has 4 players). Team
 * sports with real substitutions (soccer, cricket, volleyball, basketball)
 * default it on.
 */
const SWAPPABLE_DEFAULT_FALSE = {
  name: 'swappable',
  type: 'checkbox',
  label: 'Allow Mid-Match Player Swap',
  description: 'Let the scorer substitute a different roster player into this match while it\'s live',
  default: false,
  required: false
};

const SWAPPABLE_DEFAULT_TRUE = {
  ...SWAPPABLE_DEFAULT_FALSE,
  default: true
};

/**
 * When `swappable` is on, controls how often a swap can/must happen —
 * separate from *whether* swapping is allowed at all.
 * - AVAILABLE_ALWAYS: scorer can swap whenever they want (current default
 *   behavior wherever swappable is enabled).
 * - FORCE_ON_TOTAL_POINTS_4: MLP-specific — every 4 total points scored
 *   (both teams combined), both teams' players are forced to swap. Set on
 *   the MLP Dreambreaker match via mlpScheduler.js's overriddenMatchSettings.
 *   The actual forced-swap trigger logic in PickleballScoring is separate,
 *   later work — this constant just standardizes the value as it's plumbed
 *   from schedule generation through to the scoring component.
 */
export const SWAP_OPTIONS = {
  AVAILABLE_ALWAYS: 'SWAP_OPTION_AVAILABLE_ALWAYS',
  FORCE_ON_TOTAL_POINTS_4: 'SWAP_FORCE_ON_TOTAL_POINTS_4',
};

/**
 * Must be present in a sport's `fields` array (like `swappable` above) so its
 * value survives MatchSettingsComponent's settings round-trip instead of
 * being silently dropped — see the "Keep the full field list" comment in
 * MatchSettingsComponent.jsx for why filtering a field out of the array
 * entirely (rather than just hiding it from render) causes exactly this bug.
 * It's driven entirely by schedule generation (e.g. mlpScheduler.js sets it
 * for Dreambreaker), not a manual per-match toggle, so it's always hidden
 * from rendering in MatchSettingsComponent — never shown, even in compact
 * mode, unlike `swappable`.
 */
const SWAP_OPTION_FIELD = {
  name: 'swapOption',
  type: 'select',
  label: 'Swap Frequency',
  options: [
    { value: SWAP_OPTIONS.AVAILABLE_ALWAYS, label: 'Available anytime' },
    { value: SWAP_OPTIONS.FORCE_ON_TOTAL_POINTS_4, label: 'Forced every 4 total points (MLP Dreambreaker)' },
  ],
  default: SWAP_OPTIONS.AVAILABLE_ALWAYS,
  required: false
};

/**
 * Match Configuration Field Definitions by Sport
 * Defines the form fields needed for match configuration for each sport.
 *
 * Fields with only one option (e.g. participantsType for team-only sports) are
 * automatically hidden in the UI but their default value is still saved.
 */
const MATCH_CONFIG_FIELDS = {
  // Racquet Sports (Point-based with rally scoring)
  [SPORT_TYPES.PICKLEBALL]: {
    fields: [
      PARTICIPANTS_TYPE_BOTH,
      {
        name: 'bestOf',
        type: 'number',
        label: 'Best of',
        placeholder: 'e.g., 3 or 5',
        min: 1,
        step: 2,
        default: 1,
        required: false
      },
      {
        name: 'maxPoints',
        type: 'number',
        label: 'Maximum Points',
        placeholder: 'e.g., 11 or 15',
        min: 1,
        default: 11,
        required: false
      },
      {
        name: 'isRally',
        type: 'checkbox',
        label: 'Rally Scoring',
        description: 'Every winning rally scores a point',
        default: false,
        required: false
      },
      {
        name: 'winBy2',
        type: 'checkbox',
        label: 'Win by 2',
        description: 'Must win by at least 2 points',
        default: true,
        required: false
      },
      SWAPPABLE_DEFAULT_FALSE,
      SWAP_OPTION_FIELD
    ]
  },

  [SPORT_TYPES.TENNIS]: {
    fields: [
      PARTICIPANTS_TYPE_BOTH,
      {
        name: 'bestOf',
        type: 'number',
        label: 'Best of Sets',
        placeholder: 'e.g., 3 or 5',
        min: 1,
        step: 2,
        default: 3,
        required: false
      },
      {
        name: 'gamesPerSet',
        type: 'number',
        label: 'Games per Set',
        placeholder: 'e.g., 6',
        min: 1,
        default: 6,
        required: false
      },
      {
        name: 'tiebreakAt',
        type: 'number',
        label: 'Tiebreak At',
        placeholder: 'e.g., 6',
        min: 1,
        default: 6,
        required: false
      },
      {
        name: 'winBy2',
        type: 'checkbox',
        label: 'Win by 2',
        description: 'Must win games/sets by at least 2',
        default: true,
        required: false
      },
      SWAPPABLE_DEFAULT_FALSE
    ]
  },

  // Ping Pong: singles or doubles — replaces old 'format' field
  [SPORT_TYPES.PING_PONG]: {
    fields: [
      PARTICIPANTS_TYPE_BOTH,
      {
        name: 'bestOf',
        type: 'number',
        label: 'Best of',
        placeholder: 'e.g., 3, 5, or 7',
        min: 1,
        step: 2,
        default: 3,
        required: false
      },
      {
        name: 'maxPoints',
        type: 'select',
        label: 'Points to Win',
        options: [
          {
            value: 11,
            label: '11 Points (Modern)',
            hint: 'ITTF standard since 2001 · Serve alternates every 2 points · Deuce at 10-10'
          },
          {
            value: 21,
            label: '21 Points (Classic)',
            hint: 'Pre-2001 rules · Serve alternates every 5 points · Deuce at 20-20'
          }
        ],
        default: 11,
        required: true
      },
      {
        name: 'winBy2',
        type: 'checkbox',
        label: 'Win by 2',
        description: 'Must win by at least 2 points',
        default: true,
        required: false
      },
      SWAPPABLE_DEFAULT_FALSE
    ]
  },

  // Badminton: singles or doubles, rally scoring, 21 points, setting at 20-20, cap at 30
  [SPORT_TYPES.BADMINTON]: {
    fields: [
      PARTICIPANTS_TYPE_BOTH,
      {
        name: 'bestOf',
        type: 'number',
        label: 'Best of',
        placeholder: 'e.g., 1, 3, or 5',
        min: 1,
        step: 2,
        default: 3,
        required: false
      },
      {
        name: 'maxPoints',
        type: 'number',
        label: 'Points to Win',
        placeholder: 'e.g., 21',
        min: 1,
        default: 21,
        required: false
      },
      {
        name: 'settingCap',
        type: 'number',
        label: 'Setting Cap',
        placeholder: 'e.g., 30',
        min: 1,
        default: 30,
        required: false
      },
      {
        name: 'winBy2',
        type: 'checkbox',
        label: 'Win by 2 (in setting)',
        description: 'Must win by 2 when both reach maxPoints-1',
        default: true,
        required: false
      },
      SWAPPABLE_DEFAULT_FALSE
    ]
  },

  // Team Sports (Time-based) — teams only, participantsType auto-hidden
  [SPORT_TYPES.BASKETBALL]: {
    fields: [
      PARTICIPANTS_TYPE_TEAMS_ONLY,
      {
        name: 'bestOf',
        type: 'number',
        label: 'Best of (Matches)',
        placeholder: 'e.g., 1, 3, or 5',
        min: 1,
        step: 2,
        default: 1,
        required: false
      },
      {
        name: 'matchTime',
        type: 'number',
        label: 'Match Time (minutes)',
        placeholder: 'e.g., 48 for NBA, 40 for FIBA',
        min: 1,
        max: 240,
        default: 48,
        required: false
      },
      {
        name: 'quarters',
        type: 'number',
        label: 'Number of Quarters',
        placeholder: 'e.g., 4',
        min: 1,
        max: 8,
        default: 4,
        required: false
      },
      {
        name: 'overtimeMinutes',
        type: 'number',
        label: 'Overtime Duration (minutes)',
        placeholder: 'e.g., 5',
        min: 1,
        max: 30,
        default: 5,
        required: false
      },
      SWAPPABLE_DEFAULT_TRUE
    ]
  },

  [SPORT_TYPES.SOCCER]: {
    fields: [
      PARTICIPANTS_TYPE_TEAMS_ONLY,
      {
        name: 'matchTime',
        type: 'number',
        label: 'Match Time (minutes)',
        placeholder: 'e.g., 90',
        min: 1,
        max: 240,
        default: 90,
        required: false
      },
      {
        name: 'halves',
        type: 'number',
        label: 'Number of Halves',
        placeholder: 'e.g., 2',
        min: 1,
        max: 4,
        default: 2,
        required: false
      },
      {
        name: 'extraTimeMinutes',
        type: 'number',
        label: 'Extra Time Duration (minutes)',
        placeholder: 'e.g., 30',
        min: 1,
        max: 60,
        default: 30,
        required: false
      },
      SWAPPABLE_DEFAULT_TRUE
    ]
  },

  [SPORT_TYPES.VOLLEYBALL]: {
    fields: [
      PARTICIPANTS_TYPE_TEAMS_ONLY,
      {
        name: 'bestOf',
        type: 'number',
        label: 'Best of Sets',
        placeholder: 'e.g., 3 or 5',
        min: 1,
        step: 2,
        default: 5,
        required: false
      },
      {
        name: 'maxPoints',
        type: 'number',
        label: 'Points per Set',
        placeholder: 'e.g., 25',
        min: 1,
        default: 25,
        required: false
      },
      {
        name: 'finalSetPoints',
        type: 'number',
        label: 'Final Set Points',
        placeholder: 'e.g., 15',
        min: 1,
        default: 15,
        required: false
      },
      {
        name: 'isRally',
        type: 'checkbox',
        label: 'Rally Scoring',
        description: 'Every winning rally scores a point',
        default: true,
        required: false
      },
      {
        name: 'winBy2',
        type: 'checkbox',
        label: 'Win by 2',
        description: 'Must win by at least 2 points',
        default: true,
        required: false
      },
      SWAPPABLE_DEFAULT_TRUE
    ]
  },

  // Bat and Ball Sports — teams only; removed old 'format: team' field
  [SPORT_TYPES.CRICKET]: {
    fields: [
      PARTICIPANTS_TYPE_TEAMS_ONLY,
      {
        name: 'oversPerInning',
        type: 'number',
        label: 'Overs per Inning',
        placeholder: 'e.g., 20 for T20, 50 for ODI',
        min: 1,
        max: 50,
        default: 2,
        required: false
      },
      {
        name: 'oversPerBowler',
        type: 'number',
        label: 'Overs per Bowler',
        placeholder: 'e.g., 4',
        min: 1,
        max: 10,
        default: 2,
        required: false
      },
      {
        name: 'inningsPerTeam',
        type: 'number',
        label: 'Innings per Team',
        placeholder: 'e.g., 1 or 2',
        min: 1,
        max: 2,
        default: 1,
        required: false
      },
      {
        name: 'maxPlayersPerTeam',
        type: 'number',
        label: 'Players per Team',
        placeholder: 'e.g., 11',
        min: 1,
        max: 15,
        default: 2,
        required: false
      },
      {
        name: 'superOver',
        type: 'checkbox',
        label: 'Super Over',
        description: 'Use super over for ties',
        default: false,
        required: false
      },
      SWAPPABLE_DEFAULT_TRUE
    ]
  }
};

/**
 * Get match configuration fields for a specific sport
 * @param {string|number} sportIdentifier - Sport name or sport type ID
 * @returns {Object} - Field definitions for the sport's match configuration
 */
export const getMatchConfigurationFields = (sportIdentifier) => {
  if (!sportIdentifier) {
    return { fields: [] };
  }

  // If it's a number (sport type ID), we need to map it to sport name
  // For now, we'll use sport name directly. In the future, you might want to
  // fetch the sport name from sportTypes array or make an API call

  // Try exact match with sport name
  const normalizedSport = typeof sportIdentifier === 'string'
    ? sportIdentifier.toLowerCase()
    : sportIdentifier;

  // Find matching sport type
  const sportKey = Object.keys(SPORT_TYPES).find(key =>
    SPORT_TYPES[key] === normalizedSport ||
    key.toLowerCase() === String(normalizedSport).toLowerCase()
  );

  if (sportKey && MATCH_CONFIG_FIELDS[SPORT_TYPES[sportKey]]) {
    return MATCH_CONFIG_FIELDS[SPORT_TYPES[sportKey]];
  }

  // Default configuration for unknown sports
  return {
    fields: [
      PARTICIPANTS_TYPE_BOTH,
      {
        name: 'bestOf',
        type: 'number',
        label: 'Best of',
        placeholder: 'e.g., 3 or 5',
        min: 1,
        step: 2,
        default: 3,
        required: false
      },
      {
        name: 'maxPoints',
        type: 'number',
        label: 'Maximum Points',
        placeholder: 'e.g., 21',
        min: 1,
        default: 21,
        required: false
      }
    ]
  };
};

/**
 * ============================================================================
 * NOTE: Phase and Status Mappings
 * ============================================================================
 * Phase mappings (STATUS_TO_PHASE, getStatusToPhaseMapping, etc.) have been
 * moved to phaseConfig.js to avoid circular dependency issues.
 *
 * Import from phaseConfig.js instead:
 * import { getStatusToPhaseMapping } from './phaseConfig';
 */

// Note: Component-related exports (getScoringComponent, hasScoringCard, etc.)
// have been moved to scoringComponents.js to avoid circular dependencies
export default {
  getMatchConfigurationFields
};
